// commentModeration — lightweight client-side filter for hateful, discriminatory,
// or deliberately provocative content. Categories are kept as compact regexes
// so we can surface a specific reason to the author. This is intentionally
// strict on slurs and harassment patterns; ambiguous strings pass through.

export type ModerationCategory =
  | "slur"
  | "harassment"
  | "threat"
  | "sexual_minors"
  | "self_harm";

export interface ModerationResult {
  ok: boolean;
  category?: ModerationCategory;
  reason?: string;
}

// Patterns are written to catch common obfuscations (numbers/symbols swapped
// for letters, repeated chars, separators). We keep the source list small and
// generic — exhaustive lists are better handled server-side.
const SLUR_PATTERNS: RegExp[] = [
  // racial slurs
  /\bn[\W_]*[i1!|][\W_]*g[\W_]*g+[\W_]*(?:e[\W_]*r|a)s?\b/i,
  /\bch[i1]nk(?:s|y)?\b/i,
  /\bg[o0]+ks?\b/i,
  /\bsp[i1]cs?\b/i,
  /\bw[e3]tb[a@]cks?\b/i,
  /\bk[i1]kes?\b/i,
  /\btowel[\W_]?heads?\b/i,
  /\bsand[\W_]?n[i1]gg(?:e[r]?|a)s?\b/i,
  // homophobic / transphobic slurs
  /\bf[a@4]gg?(?:[o0]ts?|s)?\b/i,
  /\bd[i1]kes?\b/i,
  /\btr[a@]nn(?:y|ies)\b/i,
  // ableist slurs
  /\br[e3]t[a@]rds?\b/i,
];

const HARASSMENT_PATTERNS: RegExp[] = [
  /\b(kill|kys|hang|off)\s+y(?:ou)?r?self\b/i,
  /\bgo\s+die\b/i,
  /\bnobody\s+(?:loves|likes)\s+you\b/i,
  /\byou(?:'?re|\s+are)\s+(?:worthless|trash|garbage|subhuman)\b/i,
];

const THREAT_PATTERNS: RegExp[] = [
  /\bi(?:'?ll|\s+will)\s+(?:kill|hurt|find|beat|murder)\s+you\b/i,
  /\b(?:rape|murder|lynch)\s+(?:you|them|him|her)\b/i,
];

const SEXUAL_MINORS_PATTERNS: RegExp[] = [
  /\b(?:child|kid|minor|underage)\s+(?:porn|sex|nudes?)\b/i,
  /\bcp\b(?=.*\b(?:porn|video|pic)s?\b)/i,
];

const SELF_HARM_PATTERNS: RegExp[] = [
  /\bcut\s+yourself\b/i,
];

const REASONS: Record<ModerationCategory, string> = {
  slur: "This note appears to contain a slur. Hateful or discriminatory language isn't allowed.",
  harassment: "This note reads as targeted harassment. Please keep it kind.",
  threat: "Threats of violence aren't allowed.",
  sexual_minors: "Sexual content involving minors is strictly prohibited.",
  self_harm: "Encouraging self-harm isn't allowed.",
};

export function moderateComment(raw: string): ModerationResult {
  const text = (raw ?? "").normalize("NFKC");
  if (!text.trim()) return { ok: true };

  const checks: Array<[ModerationCategory, RegExp[]]> = [
    ["slur", SLUR_PATTERNS],
    ["sexual_minors", SEXUAL_MINORS_PATTERNS],
    ["threat", THREAT_PATTERNS],
    ["harassment", HARASSMENT_PATTERNS],
    ["self_harm", SELF_HARM_PATTERNS],
  ];

  for (const [category, patterns] of checks) {
    if (patterns.some((re) => re.test(text))) {
      return { ok: false, category, reason: REASONS[category] };
    }
  }
  return { ok: true };
}